import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SignaturePad,
  type SignaturePadHandle,
} from '@roger/ui-components';
import { FileText, Loader2 } from 'lucide-react';

import {
  getLoaSigningFaxNumberPortsPortOrderIdLoaSigningGet as getLoaSigning,
  signLoaFaxNumberPortsPortOrderIdLoaSignaturePost as signLoa,
} from '../../../services/api/sdk.gen';
import type {
  LoaSigningInfo,
  PortOrderDetail,
} from '../../../services/api/types.gen';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { describeError, logFailure, type Notify } from './shared';

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const base64ToPdf = (base64: string): Blob => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
};

/**
 * Signing the letter in Roger. The letter shown is the exact one the server
 * hashed, and the hash goes back with the signature, so a letter that changed
 * in between is refused rather than signed unread. The consent text is the
 * server's, shown as it is stored with the signing record.
 */
const SignLoaDialog: React.FC<{
  detail: PortOrderDetail;
  onClose: () => void;
  onSigned: (detail: PortOrderDetail) => void;
  showSuccess: Notify;
  showError: Notify;
}> = ({ detail, onClose, onSigned, showSuccess, showError }) => {
  const [info, setInfo] = useState<LoaSigningInfo | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [signed, setSigned] = useState(false);
  const [consent, setConsent] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const padRef = useRef<SignaturePadHandle>(null);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await getLoaSigning({
        path: { port_order_id: detail.id },
        throwOnError: true,
      });
      setInfo(data);
    } catch (error) {
      logFailure('Failed to load the letter to sign', error);
      setLoadFailed(true);
    }
  }, [detail.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const readLetter = () => {
    if (!info) return;
    const url = URL.createObjectURL(base64ToPdf(info.template_pdf_base64));
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleSign = async () => {
    const pad = padRef.current;
    if (!info || !pad || pad.isEmpty() || !consent || isSigning) return;
    setIsSigning(true);
    try {
      const png = await blobToBase64(await pad.toPng());
      const { data } = await signLoa({
        path: { port_order_id: detail.id },
        body: {
          signature_png: png,
          template_text_sha256: info.template_text_sha256,
          consent: true,
          consent_text_version: info.consent_text_version,
          print_name: info.print_name ?? '',
        },
        throwOnError: true,
      });
      showSuccess('Letter signed');
      onSigned(data);
      onClose();
    } catch (error) {
      logFailure('Failed to sign the letter', error);
      showError(describeError(error, 'Unable to sign the letter.'));
      // The letter or its consent may have changed: show the current one.
      setConsent(false);
      void load();
    } finally {
      setIsSigning(false);
    }
  };

  const signable = info?.signable === true;
  const printName = info?.print_name ?? '';

  return (
    <Dialog open onOpenChange={open => !open && !isSigning && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[520px]'>
        <DialogHeader>
          <DialogTitle>Sign authorization letter</DialogTitle>
          <DialogDescription>
            This letter lets Roger move your numbers from your current carrier.
          </DialogDescription>
        </DialogHeader>

        {!info && !loadFailed && (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading the letter...
          </div>
        )}
        {loadFailed && (
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            <p className='text-sm text-muted-foreground'>
              Unable to load the letter.
            </p>
            <Button variant='outline' size='sm' onClick={() => void load()}>
              Try again
            </Button>
          </div>
        )}

        {info && (
          <div className='grid gap-4'>
            <div>
              <Button variant='outline' size='sm' onClick={readLetter}>
                <FileText className='mr-2 h-4 w-4' />
                Read the letter
              </Button>
            </div>
            {!signable && (
              <Alert>
                <AlertDescription>
                  {info.reason ??
                    'Roger cannot sign this letter. Download it and sign by hand.'}
                </AlertDescription>
              </Alert>
            )}
            {signable && (
              <>
                <div className='grid gap-1.5'>
                  <Label htmlFor='loa-print-name'>Print name</Label>
                  <Input
                    id='loa-print-name'
                    value={printName}
                    disabled
                    aria-describedby='loa-print-name-hint'
                  />
                  <p
                    id='loa-print-name-hint'
                    className='text-xs text-muted-foreground'
                  >
                    The authorized signer on the details form. Sign only if you
                    are {printName} or {printName} authorized you to sign for
                    them. Otherwise, download the letter for them to sign by
                    hand.
                  </p>
                </div>
                <div className='grid gap-1.5'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium'>Signature</span>
                    <Button
                      variant='ghost'
                      size='sm'
                      disabled={isSigning || !signed}
                      onClick={() => padRef.current?.clear()}
                    >
                      Clear
                    </Button>
                  </div>
                  <SignaturePad
                    ref={padRef}
                    ariaLabel='Signature'
                    disabled={isSigning}
                    onChange={setSigned}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Draw with your finger or mouse. Date:{' '}
                    {formatDateForDisplay(info.signed_on)}
                  </p>
                </div>
                <div className='flex items-start gap-2'>
                  <Checkbox
                    id='loa-consent'
                    className='mt-0.5'
                    checked={consent}
                    disabled={isSigning}
                    onCheckedChange={value => setConsent(value === true)}
                  />
                  <Label
                    htmlFor='loa-consent'
                    className='font-normal leading-5'
                  >
                    {info.consent_text}
                  </Label>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onClose} disabled={isSigning}>
            Cancel
          </Button>
          {signable && (
            <Button
              onClick={() => void handleSign()}
              disabled={!signed || !consent || isSigning}
            >
              {isSigning && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Sign letter
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SignLoaDialog;
